import Vue from 'vue';
import { Component , Mixins } from 'vue-property-decorator';
import { required, maxLength, decimal, between  } from 'vuelidate/lib/validators';
import vueDropzone from 'vue2-dropzone';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import KEYS from '@/values/keys';
import { SECTION_TYPES } from '@/content/values/sections';
import { CreateObjectsMixin } from '@/courses/mixins/create-objects.mixin';
import { SectionsProvider } from '@/content/providers/sections.provider';
import { RequestProvider } from '@/shared/providers/request.provider';

@Component({
  name: 'ed-lesson-create-file',
  components: {
    vueDropzone
  }
})
export default class EdLessonCreateFile extends Mixins(CreateObjectsMixin) {

  /* Data */
  public dropzoneMode: string = KEYS.DROPZONE_CONFIG.SINGLE;
  private section: any = null;
  public extension: string = '';
  public form: any = EdFormModel([
    EdInputModel('name', '', { required }),
    EdInputModel('description', ''),
    EdInputModel('homework', false, {}, true)
  ]);
  public dropOptions: any = {
    url: 'http://eduac/upload',
    autoProcessQueue: true,
    acceptedFiles: 'application/pdf,.docx,.doc,.xlsx,.xls',
    addRemoveLinks: true,
    uploadMultiple: false,
    maxFiles: 1,
    maxFilesize: 200,
    parallelUploads: 1,
    createImageThumbnails: false,
    timeout: 600000
  }
  public title: string = '';
  public isHomeworkResponse: boolean = false;
  public canSetHomework: boolean = true;
  public uploadingFile: boolean = false;
  public hasErrorFile: boolean = false;
  public requestDataForm: any = {};
  public file: any = null;

  /* Computed */
  get showDropzone() {
    return !this.file || this.uploadingFile;
  }

  /* Methods */
  private init(name = '', homework = false, grade = '') {
    this.form = EdFormModel([
      EdInputModel('name', name, { required, maxLength: maxLength(60) }),
      EdInputModel('homework', homework, {}, true),
      EdInputModel('grade', grade, { decimal, between: between(0, 100)})
    ]);
    if(!this.hasGradeField) {
      this.form.setValid('grade', true);
    }
    if(this.section !== null && ('type') in this.section){
      this.extension = SectionsProvider.getExtensionFromSectionType(this.section.type);
    }
  }

  public onFileUploadedSuccess({ file, res }) {
    this.extension = this.getExtension(file.name);
    if(this.dropOptions.autoProcessQueue) {
      this.section.type = this.getSectionTypeFromExtension(this.extension);
      this.section.text = res.section.text;
      this.$Courses.Store.updateSection(this.section);
      this.file = file;
      this.extension = this.getExtension(file.name);
    } else {
      let data = {
        type: this.getSectionTypeFromExtension(this.extension),
        name: this.form.name.value,
        description: '',
        parentId: this.params.parentId
      }
      this.$edEventBus.$emit(this.params.modalBusSaveEvent, file, res, data);
    }
  }

  private getSectionTypeFromExtension(extension = '') {
    let sectionType = '';
    switch(extension.toUpperCase()) {
      case KEYS.FILES_TYPE.DOC:
        sectionType = SECTION_TYPES.LESSON_DOC.code;
      break;
      case KEYS.FILES_TYPE.DOCX:
        sectionType = SECTION_TYPES.LESSON_DOC.code;
      break;
      case KEYS.FILES_TYPE.PDF:
        sectionType = SECTION_TYPES.LESSON_PDF.code;
      break;
      case KEYS.FILES_TYPE.XLS:
        sectionType = SECTION_TYPES.LESSON_XLS.code;
      break;
      case KEYS.FILES_TYPE.XLSX:
        sectionType = SECTION_TYPES.LESSON_XLS.code;
      break;
      case KEYS.FILES_TYPE.EPUB:
        sectionType = SECTION_TYPES.LESSON_EPUB.code;
      break;
      case KEYS.FILES_TYPE.PPTX:
        sectionType = SECTION_TYPES.LESSON_PPT.code;
      break;
      case KEYS.FILES_TYPE.PPT:
        sectionType = SECTION_TYPES.LESSON_PPT.code;
      break;
    }
    return sectionType;
  }

  private getExtension(filename) {
    let fileArr = filename.split('.');
    return fileArr[fileArr.length - 1] ? fileArr[fileArr.length - 1] : '';
  }

  public closeModal() {
    this.$EdModal.closeModal();
  }

  public saveFile() {
    this.hasErrorFile = false;
    if(this.dropOptions.autoProcessQueue) {
      let data = {
        ...this.section,
        name: this.form.name.value,
        description: '',
        parentId: this.params.parentId,
        homework: this.form.homework.value
      };
      this.$edEventBus.$emit(this.params.modalBusSaveEvent, data, this.params);
    } else {
      let nameParam = 'requestDataName' in this.params ?  this.params.requestDataName : 'homework';
      let type = nameParam == 'section' ? SECTION_TYPES.LESSON_TEMP_FILE.code : this.getSectionTypeFromExtension(this.extension);
      let base = {
        name: this.form.name.value,
        description: '',
        type,
        fileUpload: true
      };
      if(!this.params.formatData) {
        this.requestDataForm[nameParam] = {
          ...base,
          parentId: this.params.parentId,
          homework: this.form.homework.value
        }
        if(('section' in this.params) && ('isResponse' in this.params.section)){
          this.requestDataForm.isResponse = this.params.section.isResponse;
        }
      } else {
        this.requestDataForm = this.params.formatData(base);
      }
      this.uploadingFile = true;
      (this.$refs.dropzoneRef as any).processQueue(this.requestDataForm).finally(()=>{
        if(this.hasGradeField)
          this.refreshSelectedStudentWidget();
      });
    }
  }

  public removeFile() {
    if(this.dropOptions.autoProcessQueue) {
      (this.$root.$children[0].$refs.EdLoading as any).show(this.$i18n.t('common.deleting'));
      this.$Courses.Store.removeFileA(this.section)
      .then(() => {
        this.section.text = '';
        this.file = null;
      }).finally(() => {
        (this.$root.$children[0].$refs.EdLoading as any).hide();
      });
    } else {
      (this.$refs.dropzoneRef as any).removeAllFiles();
      this.file = null;
    }
  }

  public onFileAdd(file) {
    this.hasErrorFile = false;
    setTimeout(() => {
      if(file.accepted && !this.dropOptions.autoProcessQueue) {
        let nameParts = file.name.split('.');
        let name = file.name.replace(`.${nameParts[nameParts.length - 1]}`, '').slice(0, 60);
        Vue.set(this.form, 'name', {
          valid: true,
          validators: { required, maxLength: maxLength(60) },
          value: name
        });
        this.extension = this.getExtension(file.name);
        this.file = file;
      }
    }, 0);
  }

  public onResetFile() {
    this.uploadingFile = false;
    this.file = null;
  }

  public onErrorFile() {
    this.uploadingFile = false;
    this.hasErrorFile = true;
    (this.$refs.dropzoneRef as any).removeAllFiles();
    this.file = null;
  }

  /* Hooks */
  created() {
    this.dropOptions = {...this.params.dropOptions, headers: RequestProvider.getTokenHeader() };
    this.title = this.params.titleModal || '';
    this.isHomeworkResponse = this.params.isHomeworkResponse;
    this.canSetHomework = 'canSetHomework' in this.params ? this.params.canSetHomework : true;
    this.section = null;
    this.$EdModal.$on(`${this.params.modalBusSaveEvent}_CLOSE`, this.closeModal);
    this.init();
  }

}